/**
 * CORE-BL — the "you finished it" notice.
 *
 * WHY THIS IS ITS OWN COMPONENT
 * -----------------------------
 * It used to be an `AlertDialog` with local `showCompletionDialog` state
 * inside `Coachmark`. Pressing Complete set `completed`, which makes
 * `isActive` false — and `GlobalTutorialBar` renders coachmarks under
 * `isActive && steps.map(...)`. So the parent unmounted every Coachmark at
 * the exact moment the walkthrough completed, and the dialog went with it.
 * The person pressed Complete and the screen just went quiet.
 *
 * No amount of guarding inside `Coachmark` could fix that: a child cannot
 * outlive its parent unmounting it. The notice is therefore raised by
 * `TutorialContext` when completion is recorded, and rendered here from a
 * mount point that is not conditional on `isActive`.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTutorial } from '@/contexts/TutorialContext';

export const TutorialCompletionNotice = () => {
  const { completionNotice, acknowledgeCompletion } = useTutorial();

  if (!completionNotice) return null;

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) acknowledgeCompletion();
      }}
    >
      <AlertDialogContent data-testid="tutorial-completion-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {`“${completionNotice.label}” complete 🎉`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            You have been through every step of this walkthrough. It stays
            available &mdash; you can replay it whenever you want it, from the
            help button.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            data-testid="tutorial-completion-ack"
            onClick={acknowledgeCompletion}
          >
            Got it, thanks!
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
